import React, { useMemo, useCallback } from "react"
import { useDispatch } from "react-redux"
import { Button, notification } from "antd"
import { Table } from "components"
import { API, Endpoints } from "utils/api"
import { setLoader } from "store/actions"
import Strings from "utils/strings"

// Types and Interfaces matching MongoDB Schema
interface MasterData {
	_id: string
	businessId: string
	importedId: string
	name: string
	phone?: string
	email?: string
	address: {
		address?: string
		number?: string
		floor?: string
		postalCode?: string
		location?: {
			lat?: number
			lng?: number
		}
		city?: string
		addressNotes?: string
	}
	addressWarnings: {
		address: boolean
		postCode: boolean
		coordinates: boolean
	}
	addressErrors: {
		address: boolean
		postCode: boolean
		coordinates: boolean
	}
	slots?: {
		slot1Start?: number
		slot1End?: number
		slot2Start?: number
		slot2End?: number
	}
	fileName?: string
	fileSize?: number
	fileLocation?: string
	uploadedBy: string
	_created: string
	_modified: string
}

interface PendingUsersTableProps {
	users: MasterData[]
	setShowImportModal: (show: boolean) => void
	setShowAddressValidationModal: (userId: string) => void
	refreshData: () => void
}

const PendingUsersTable: React.FC<PendingUsersTableProps> = ({
	users,
	setShowImportModal,
	setShowAddressValidationModal,
	refreshData,
}) => {
	const dispatch = useDispatch()

	// Memoized handlers
	const handleApproveUser = useCallback(
		async (userId: string) => {
			await approveUser(dispatch, userId, refreshData)
		},
		[dispatch, refreshData]
	)

	const handleDeleteRow = useCallback(
		async (userId: string) => {
			await deleteRow(dispatch, userId, refreshData)
		},
		[dispatch, refreshData]
	)

	const handleAddressValidation = useCallback(
		(userId: string) => {
			setShowAddressValidationModal(userId)
		},
		[setShowAddressValidationModal]
	)

	// Memoized columns configuration
	const columns = useMemo(
		() => [
			{
				Header: "Issues",
				id: "warnings",
				accessor: "_id",
				Cell: ({ row }: { row: { original: MasterData } }) => {
					const addressErrors = row.original.addressErrors || {}
					const addressWarnings = row.original.addressWarnings || {}
					const issues: string[] = []

					// Check for errors (red flags)
					if (addressErrors?.address) issues.push("Error: Address")
					if (addressErrors?.postCode) issues.push("Error: PostCode")
					if (addressErrors?.coordinates) issues.push("Error: Coordinates")

					// Check for warnings (yellow flags)
					if (addressWarnings?.address) issues.push("Warning: Address")
					if (addressWarnings?.postCode) issues.push("Warning: PostCode")
					if (addressWarnings?.coordinates) issues.push("Warning: Coordinates")

					return issues.length > 0 ? (
						<div
							style={{
								color:
									addressErrors?.address ||
									addressErrors?.postCode ||
									addressErrors?.coordinates
										? "#ff4d4f"
										: "#faad14",
							}}
						>
							{issues.join(", ")}
						</div>
					) : (
						<span style={{ color: "#52c41a" }}>Clean</span>
					)
				},
			},
			{
				Header: "Business ID",
				id: "business",
				accessor: "businessId.name",
			},
			{
				Header: "Imported ID",
				id: "importedId",
				accessor: "importedId",
				Cell: ({ row }: { row: { original: MasterData } }) => {
					const original = row.original
					return original.importedId || "-"
				},
			},
			{
				Header: "Name",
				id: "name",
				accessor: "name",
				// Cell: ({ original }: { original: MasterData }) => original.name || "-",
			},
			{
				Header: "Email",
				id: "email",
				accessor: "email",
				// Cell: ({ original }: { original: MasterData }) => original.email || "-",
			},
			{
				Header: "Phone",
				id: "phone",
				accessor: "phone",
				// Cell: ({ original }: { original: MasterData }) => original.phone || "-",
			},
			{
				Header: "Address",
				id: "address",
				accessor: "address.address",
			},
			{
				Header: "Number",
				id: "number",
				accessor: "address.number",
			},
			{
				Header: "Floor",
				id: "floor",
				accessor: "address.floor",
			},
			{
				Header: "Postal Code",
				id: "postalCode",
				accessor: "address.postalCode",
			},
			{
				Header: "City",
				id: "city",
				accessor: "address.city",
			},
			{
				Header: "Coordinates",
				id: "coordinates",
				accessor: "address",
				Cell: ({ row }: { row: { original: MasterData } }) => {
					const location = row.original.address?.location
					if (!location?.lat || !location?.lng) return "-"
					return `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`
				},
			},
		],
		[]
	)

	// Memoized actions configuration
	const actions = useMemo(
		() => ({
			edit: (original: MasterData) => ({
				onClick: () => handleAddressValidation(original._id),
				disabled: !(
					original.addressErrors?.address ||
					original.addressErrors?.postCode ||
					original.addressErrors?.coordinates ||
					original.addressWarnings?.address ||
					original.addressWarnings?.postCode ||
					original.addressWarnings?.coordinates
				),
			}),
			approve: (original: MasterData) => ({
				onClick: () => handleApproveUser(original._id),
				disabled:
					original.addressErrors?.address ||
					original.addressErrors?.postCode ||
					original.addressErrors?.coordinates,
			}),
			delete: (original: MasterData) => ({
				onClick: () => handleDeleteRow(original._id),
			}),
		}),
		[handleAddressValidation, handleApproveUser, handleDeleteRow]
	)

	return (
		<div>
			<div
				style={{
					marginBottom: 16,
					display: "flex",
					gap: 16,
					alignItems: "center",
					flexWrap: "wrap",
				}}
			>
				<Button type="primary" onClick={() => setShowImportModal(true)}>
					Import Master Data
				</Button>
			</div>

			<Table
				title={{
					icon: "upload-file",
					title: `Pending Users (${users.length})`,
				}}
				data={users}
				columns={columns}
				actions={actions}
				paginated
			/>
		</div>
	)
}

// Async operations with modern error handling
const approveUser = async (
	dispatch: any,
	userId: string,
	onSuccess: () => void = () => {}
): Promise<void> => {
	dispatch(setLoader(true))

	try {
		const response = await API.post({
			url: Endpoints.uriMasterData(`approve/${userId}`),
		})

		if (response.ok) {
			notification.success({
				message: "Master Data",
				description: "User approved successfully",
				placement: "bottomRight",
				duration: 3,
			})
			onSuccess()
		} else {
			throw new Error(response.data?.message || "Failed to approve user")
		}
	} catch (error) {
		const message =
			error instanceof Error ? error.message : Strings.serverErrors.wentWrong

		notification.error({
			message: "Master Data",
			description: message,
			placement: "bottomRight",
			duration: 5,
		})
	} finally {
		dispatch(setLoader(false))
	}
}

const deleteRow = async (
	dispatch: any,
	id: string,
	onSuccess: () => void = () => {}
): Promise<void> => {
	dispatch(setLoader(true))

	try {
		const response = await API.delete({
			url: Endpoints.uriMasterData(`${id}`),
		})

		if (response.ok) {
			notification.success({
				message: "Master Data",
				description: "Row deleted successfully",
				placement: "bottomRight",
				duration: 3,
			})
			onSuccess()
		} else {
			throw new Error(response.data?.message || "Failed to delete row")
		}
	} catch (error) {
		const message =
			error instanceof Error ? error.message : Strings.serverErrors.wentWrong

		notification.error({
			message: "Master Data",
			description: message,
			placement: "bottomRight",
			duration: 5,
		})
	} finally {
		dispatch(setLoader(false))
	}
}

export default PendingUsersTable
